import { useMutation, useQueryClient } from '@tanstack/react-query';
import produce from 'immer';

import {
  GET_SESSION_BY_ID,
  UPDATE_CONTEXT_BY_SESSION_ID,
  type QuerySessionResponse,
  type SessionUpdate,
} from '../../contracts';
import { updateSessionById } from '../api/session';

type MutationInput = {
  context: SessionUpdate['clinician_notes'];
  sessionId: string;
};

export type UpdateContextContentPorts = {
  onSuccess?: (
    data: Awaited<ReturnType<typeof updateSessionById>>,
    input: MutationInput
  ) => void;
  onError?: (error: Error, variables: MutationInput) => void;
};

export function useMutateUpdateContextContent({
  onSuccess,
  onError,
}: UpdateContextContentPorts = {}) {
  const queryClient = useQueryClient();

  const getSessionKey = GET_SESSION_BY_ID;

  return useMutation({
    mutationKey: [UPDATE_CONTEXT_BY_SESSION_ID],
    mutationFn: async ({ context, sessionId }: MutationInput) => {
      return await updateSessionById(sessionId, {
        clinician_notes: context,
      });
    },
    onMutate: ({ context, sessionId }) => {
      queryClient.setQueryData<QuerySessionResponse>(
        [getSessionKey, { sessionId }],
        (oldData) => {
          if (!oldData || !context) {
            return undefined;
          }

          return produce(oldData, (draft) => {
            if (context) {
              draft.clinician_notes = context;
              // Only show sync button if note has already been generated
              if (draft.consult_note?.result) {
                draft.shouldShowNoteSyncChanges = true;
              }
            }
          });
        }
      );
    },
    onSuccess,
    onError: (error, variables) => {
      const { sessionId } = variables;
      void queryClient.invalidateQueries({
        queryKey: [getSessionKey, { sessionId }],
      });
      onError?.(error, variables);
    },
  });
}
